import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Slide from '@mui/material/Slide';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type ToastItem, type ToastTone, useToastStore } from '@/shared/lib';
import { fonts, radius } from '@/shared/theme';
import { Icon, type IconName } from './icon';

const toneIcon: Record<ToastTone, IconName> = {
  success: 'check',
  error: 'alert',
  warning: 'alert',
  info: 'info',
};

/** Bottom-right toast stack in the prototype's style. Mount once at the app root. */
export function Toaster() {
  const items = useToastStore((s) => s.items);

  return (
    <Box
      aria-live="polite"
      sx={{
        position: 'fixed',
        right: { xs: 12, sm: 22 },
        bottom: { xs: 12, sm: 22 },
        left: { xs: 12, sm: 'auto' },
        zIndex: (theme) => theme.zIndex.snackbar,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1.25,
        pointerEvents: 'none',
      }}
    >
      {items.map((item) => (
        <ToastCard key={item.id} item={item} />
      ))}
    </Box>
  );
}

function ToastCard({ item }: { item: ToastItem }) {
  const { t } = useTranslation();
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    if (!item.duration) return;
    const timer = window.setTimeout(() => dismiss(item.id), item.duration);
    return () => window.clearTimeout(timer);
  }, [item.id, item.duration, dismiss]);

  return (
    <Slide direction="up" in mountOnEnter>
      <Box
        role={item.tone === 'error' ? 'alert' : 'status'}
        sx={(theme) => ({
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          width: 390,
          maxWidth: '100%',
          p: '14px 15px',
          background: theme.vars.palette.md.panel,
          border: `1px solid ${theme.vars.palette.md.border}`,
          borderRadius: radius.card,
          boxShadow: theme.vars.palette.md.shadowLg,
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            flex: 'none',
            mt: '1px',
            color: {
              success: theme.vars.palette.md.ok,
              error: theme.vars.palette.md.bad,
              warning: theme.vars.palette.md.warnInk,
              info: theme.vars.palette.md.blueInk,
            }[item.tone],
          })}
        >
          <Icon name={toneIcon[item.tone]} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontFamily: fonts.head, fontWeight: 700, fontSize: 13.5 }}>{item.title}</Box>
          {item.text && (
            <Box
              sx={{
                fontSize: 12.5,
                color: 'text.secondary',
                mt: '3px',
                lineHeight: 1.45,
                overflowWrap: 'anywhere',
              }}
            >
              {item.text}
            </Box>
          )}
        </Box>
        <ButtonBase
          aria-label={t('actions.close')}
          onClick={() => dismiss(item.id)}
          sx={(theme) => ({
            flex: 'none',
            width: 28,
            height: 28,
            borderRadius: radius.sm,
            color: theme.vars.palette.md.muted,
            '&:hover': {
              background: `color-mix(in srgb, ${theme.vars.palette.md.text} 8%, transparent)`,
            },
          })}
        >
          <Icon name="x" size={16} />
        </ButtonBase>
      </Box>
    </Slide>
  );
}
